/**
 * Time Machine #114 — pure check: is the slider sitting at the exact
 * moment that `enterTimeTravel` seeds it (LOCAL end-of-day)?
 *
 * Used by the auto-snap-to-last-event-on-day-entry useEffect in
 * `modelTS.context.js`. `enterTimeTravel` (in `app.context.js`) sets the
 * slider to `new Date(yyyy, mm, dd, 23, 59, 59, 999)` — LOCAL components.
 * The snap-to-last-event guard must compare against the SAME LOCAL value
 * to detect "user hasn't manually scrubbed yet, fire the snap."
 *
 * Pre-fix bug (caught by code review on PR #118): the inline guard used
 * `Date.UTC(...23, 59, 59, 999)` which produces a value 5 hours offset
 * from LOCAL EST end-of-day. For non-UTC users (Keller is US-based, so
 * all of them), the values never matched → the guard always tripped its
 * early return → the snap never fired → empty space to the right of the
 * slider on every day-entry, silently breaking the Phase 4.5 design
 * promise ("snap to last event on day entry, no empty space to the right
 * of the slider").
 *
 * Extracted as a pure utility per CLAUDE.md "extract critical-path logic":
 *   - Part of a change-detection / silent-failure-prone code path → extract
 *   - Tested → extract so tests import the real thing
 *   - Replaces inline date-component logic that was easy to get wrong → extract
 *
 * @param {Date} sliderTime — the current `timeTravelSliderTime` value
 * @param {Date} timeTravelDate — the current `timeTravelDate` value (a
 *        Date built via `new Date(yyyy, mm, dd, 0, 0, 0, 0)` representing
 *        local-midnight of the requested calendar day, same encoding
 *        `enterTimeTravel` uses)
 * @returns {boolean} true if `sliderTime` is exactly LOCAL end-of-day for
 *        `timeTravelDate`'s local YYYY-MM-DD; false for any mismatch or
 *        invalid input. Defensive: never throws.
 */
export function isSliderAtLocalDayEnd(sliderTime, timeTravelDate) {
  if (!(sliderTime instanceof Date)) return false;
  if (!(timeTravelDate instanceof Date)) return false;
  if (isNaN(sliderTime.getTime())) return false;
  if (isNaN(timeTravelDate.getTime())) return false;
  const dayEndLocalMs = new Date(
    timeTravelDate.getFullYear(),
    timeTravelDate.getMonth(),
    timeTravelDate.getDate(),
    23,
    59,
    59,
    999
  ).getTime();
  return sliderTime.getTime() === dayEndLocalMs;
}
